// ============================================================
// ClientNotesFeed.tsx
// The Client Notes tab — Layout B of the approved mockup
// (outputs/casevo-client-notes-feed.html, "CA93B7_1"): the note FEED on the
// left (composer → "All notes"), a sticky PINNED RAIL on the right (pinned
// notes + the non-billable reminder). Collapses to one column below xl, with
// the rail on top of the feed.
//
// Live backend: /api/clients/:clientId/notes (clientNoteController.ts).
//   • list / create / edit / pin / delete, with the server's access rules:
//     body edits + delete are author/admin only; pin/unpin is anyone.
//   • attach: existing client documents or a fresh upload (saved to the
//     client's documents, then attached); link case: this client's cases the
//     viewer can open (getCasesForClient is audience-filtered). ≤20 of each.
//   • show-but-lock: restricted links/files come back as "Restricted case" /
//     "Restricted file" — non-clickable, padlock, tooltip. An edit never
//     drops them (the server re-adds them; we only resend what we can see).
//   • live-sync: another member's clientNote event for THIS client refetches.
//
// Visual values are copied from the mockup via Tailwind arbitrary syntax.
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SideScroll from '../shared/SideScroll'
import DOMPurify from 'dompurify'
import {
  listClientNotesApi,
  createClientNoteApi,
  updateClientNoteApi,
  deleteClientNoteApi,
  fetchDocumentSearchApi,
  requestUploadUrlFlatApi,
  completeFileUploadApi,
  deleteFileFlatApi,
  requestDownloadUrlFlatApi,
} from '../../api/casevoApi'
import type { ClientNote, ClientNoteAttachment, ClientNoteCaseLink } from '../../types/clientNote.types'
import { useLivePermissions } from '../../hooks/useLivePermissions'
import { useFirmChange } from '../../utils/liveEvents'
import type { DocumentSearchResult } from '../../types/documentSearch.types'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { presence } from '../../services/presence'
import { isInlineSafeMime, openUrlInTab, triggerBlobDownload } from '../../utils/documentAccess'
import RichTextEditor, { NOTE_RICH_CLS } from '../shared/RichTextEditor'
import UserAvatar from '../shared/UserAvatar'
import { LockIcon, LOCKED_CASE_TIP } from '../shared/CaseLink'

// ── limits (mirror backend MAX_LINKS / MAX_CONTENT) ─────────
const MAX_LINKS = 20
const MAX_CONTENT = 100000
const LOCKED_FILE_TIP = "This file is on a case you don't have access to"
// Notes paging (mirror backend NOTES_PAGE_DEFAULT / NOTES_PAGE_MAX).
const NOTES_PAGE = 50
const NOTES_PAGE_MAX = 100

// ── mockup classes ──────────────────────────────────────────
const CARD =
  'bg-casevo-surface border border-casevo-border rounded-[14px] ' +
  'shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
// .btn.sm
const BTN_SM =
  'inline-flex items-center gap-[7px] h-[30px] px-[11px] rounded-[8px] text-[12.5px] font-medium whitespace-nowrap border transition-colors casevo-focus'
const BTN_DEFAULT = `${BTN_SM} border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid`
const BTN_PRI =
  `${BTN_SM} !font-semibold border-casevo-primary bg-casevo-primary text-white hover:bg-casevo-primary-hover ` +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-casevo-primary'
const BTN_GHOST =
  `${BTN_SM} border-transparent bg-transparent text-casevo-brand hover:bg-casevo-mint disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent`
const BTN_DANGER =
  `${BTN_SM} bg-casevo-surface text-[#b42318] border-[#f1c2bd] dark:text-casevo-danger dark:border-[#6b3434] hover:border-[#b42318] disabled:opacity-50`
// .ibtn (30px icon button)
const IBTN =
  'w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand transition-colors casevo-focus'
const IBTN_PIN_ON = '!text-casevo-gold hover:!bg-[rgba(224,182,74,0.14)]'
const IBTN_DEL = 'hover:!bg-[#fdecec] hover:!text-[#b42318] dark:hover:!bg-[rgba(127,29,29,0.35)] dark:hover:!text-casevo-danger'
// .chip
const CHIP =
  'inline-flex items-center gap-[7px] max-w-full h-[30px] px-[10px] rounded-[8px] border text-[12.5px] no-underline'
const CHIP_FILE = `${CHIP} border-casevo-border bg-casevo-surface text-casevo-forest`
const CHIP_CASE = `${CHIP} border-transparent bg-casevo-mint text-casevo-forest dark:text-[#cfeedd] font-medium`
const CHIP_CASE_BTN = `${CHIP_CASE} hover:border-casevo-mid transition-colors casevo-focus`
const CHIP_LOCKED = `${CHIP} border-dashed border-casevo-border bg-transparent text-casevo-text-soft opacity-70 cursor-not-allowed select-none font-normal`
const CHIP_X =
  'ml-[2px] w-[18px] h-[18px] rounded-[5px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-forest casevo-focus'
// .pop
const POP =
  'absolute z-30 left-0 top-[calc(100%+6px)] w-[340px] max-w-[calc(100vw-48px)] bg-casevo-surface border border-casevo-border rounded-[12px] p-2 ' +
  'shadow-[0_12px_32px_rgba(16,42,29,0.16)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.45)]'
const POP_SEARCH =
  'flex items-center gap-[7px] h-[34px] px-[10px] mb-[6px] border border-casevo-border rounded-[9px] bg-casevo-input text-casevo-text-soft text-[13px] focus-within:border-casevo-mid'
const OPT =
  'flex items-center gap-[10px] w-full px-[10px] py-2 rounded-[8px] bg-transparent text-left text-[13.5px] text-casevo-forest hover:bg-casevo-input casevo-focus'
const OPT_SMALL = 'block text-casevo-text-soft text-[11.5px]'
const GOLD_TEXT = 'text-[#8a6d18] dark:text-casevo-gold'

// ── icons (mockup symbols) ──────────────────────────────────
export const RESTRICTED_NOTE = 'Restricted note — linked to a case you can’t open'
const RestrictedNoteLine: React.FC<{ className?: string; label?: string }> = ({ className = '', label }) => (
  <p className={`m-0 text-[13.5px] italic text-casevo-text-soft ${className}`} data-restricted-note>{label ?? RESTRICTED_NOTE}</p>
)

const Svg: React.FC<{ size?: number; className?: string; sw?: number; children: React.ReactNode }> = ({ size = 15, className, sw = 1.8, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className ?? ''}`} aria-hidden="true">{children}</svg>
)
const IconPin: React.FC<{ filled?: boolean; size?: number; className?: string }> = ({ filled, size = 16, className }) => (
  <Svg size={size} className={className}><path d="M9 4h6l-1 5 3 3v2H7v-2l3-3z" fill={filled ? 'currentColor' : 'none'} /><path d="M12 14v6" /></Svg>
)
const IconClip: React.FC = () => <Svg><path d="M20 11.5l-7.8 7.8a5 5 0 01-7.1-7.1l7.8-7.8a3.3 3.3 0 014.7 4.7l-7.8 7.8a1.7 1.7 0 01-2.4-2.4l7.1-7.1" /></Svg>
const IconLink: React.FC = () => <Svg><path d="M10 14a4.5 4.5 0 006.4 0l3-3a4.5 4.5 0 00-6.4-6.4l-1.2 1.2" /><path d="M14 10a4.5 4.5 0 00-6.4 0l-3 3a4.5 4.5 0 006.4 6.4l1.2-1.2" /></Svg>
const IconCase: React.FC<{ size?: number }> = ({ size = 15 }) => <Svg size={size}><rect x="3.5" y="7" width="17" height="12.5" rx="2" /><path d="M9 7V5h6v2M3.5 12.5h17" /></Svg>
const IconDoc: React.FC<{ size?: number }> = ({ size = 15 }) => <Svg size={size}><path d="M6 3.5h8l4 4V20a.5.5 0 01-.5.5h-11A.5.5 0 016 20z" /><path d="M14 3.5V8h4" /><path d="M9 13h6M9 16.5h4" /></Svg>
const IconImg: React.FC<{ size?: number }> = ({ size = 15 }) => <Svg size={size}><rect x="3.5" y="4.5" width="17" height="15" rx="2" /><circle cx="9" cy="10" r="1.8" /><path d="M20.5 16l-5-5-8 8.5" /></Svg>
const IconEdit: React.FC = () => <Svg><path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" /></Svg>
const IconTrash: React.FC = () => <Svg><path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 13h9l1-13M10 11v6M14 11v6" /></Svg>
const IconX: React.FC = () => <Svg size={11} sw={2}><path d="M6 6l12 12M18 6L6 18" /></Svg>
const IconSearch: React.FC = () => <Svg size={14}><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></Svg>
const IconUpload: React.FC = () => <Svg size={16}><path d="M12 16V4M7 9l5-5 5 5" /><path d="M4 16v3.5A.5.5 0 004.5 20h15a.5.5 0 00.5-.5V16" /></Svg>
const IconDownload: React.FC = () => <Svg size={13} sw={2}><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></Svg>
const IconCheck: React.FC = () => <Svg sw={2.2}><path d="M5 12.5l4.5 4.5L19 7.5" /></Svg>
const IconNote: React.FC = () => <Svg size={24} sw={1.7}><path d="M6 3.5h9l4 4V20a.5.5 0 01-.5.5h-12A.5.5 0 016 20z" /><path d="M14.5 3.5V8h4.5M9 12h7M9 16h5" /></Svg>
const FileIcon: React.FC<{ mime: string; size?: number }> = ({ mime, size }) =>
  mime.startsWith('image/') ? <IconImg size={size} /> : <IconDoc size={size} />

// ── helpers ─────────────────────────────────────────────────
export interface NoteCaseOption {
  id: string
  label: string
  ref: string
  closed: boolean
}
interface FileMeta {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  uploading?: boolean
  // Uploaded here, not picked from the client's documents: the server stores it
  // on the note's case when the note has one.
  uploaded?: boolean
}

const isBlankHtml = (html: string): boolean =>
  html.replace(/<[^>]*>/g, '').replace(/&nbsp;| /g, ' ').trim().length === 0

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
const hhmm = (d: Date): string => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
// "Today, 09:41" · "Yesterday, 16:05" · "12 Feb 2026, 11:30"
const formatWhen = (iso: string): string => {
  const d = new Date(iso)
  const now = new Date()
  const y = new Date(now)
  y.setDate(now.getDate() - 1)
  if (sameDay(d, now)) return `Today, ${hhmm(d)}`
  if (sameDay(d, y)) return `Yesterday, ${hhmm(d)}`
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${hhmm(d)}`
}
// Rail: "18 Feb" / "Today" / "Yesterday"
const formatShort = (iso: string): string => {
  const d = new Date(iso)
  const now = new Date()
  const y = new Date(now)
  y.setDate(now.getDate() - 1)
  if (sameDay(d, now)) return 'Today'
  if (sameDay(d, y)) return 'Yesterday'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) })
}

// Server order: pinned first → newest pin → newest note.
const sortNotes = (list: ClientNote[]): ClientNote[] =>
  [...list].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    if (a.pinned && b.pinned) {
      const pa = a.pinnedAt ? Date.parse(a.pinnedAt) : 0
      const pb = b.pinnedAt ? Date.parse(b.pinnedAt) : 0
      if (pa !== pb) return pb - pa
    }
    const d = Date.parse(b.createdAt) - Date.parse(a.createdAt)
    // id tie-break mirrors the server's keyset order (createdAt desc, id desc).
    return d !== 0 ? d : (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)
  })

const statusOf = (err: unknown): number | undefined => (err as { response?: { status?: number } })?.response?.status
const errMsg = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { error?: string } }; message?: string }
  return e?.response?.data?.error || (err instanceof Error && err.message) || fallback
}

const openAttachment = async (fileId: string): Promise<void> => {
  const { downloadUrl, filename, mimeType } = await requestDownloadUrlFlatApi(fileId, true)
  if (isInlineSafeMime(mimeType)) openUrlInTab(downloadUrl)
  else await triggerBlobDownload(downloadUrl, filename)
}
// Download — the same flow as FileList's Download action: an attachment-
// disposition signed URL (access-checked server-side) → blob → <a download>.
const downloadAttachment = async (fileId: string): Promise<void> => {
  const { downloadUrl, filename } = await requestDownloadUrlFlatApi(fileId)
  await triggerBlobDownload(downloadUrl, filename)
}

// Close a popover on outside-mousedown / Escape.
const useDismiss = (open: boolean, ref: React.RefObject<HTMLElement | null>, onClose: () => void): void => {
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, ref, onClose])
}

// ── Pickers ─────────────────────────────────────────────────
// Attach: upload a new document (saved to the client, then attached) or pick
// an existing one of THIS client's documents (document search, clientId).
const FilePicker: React.FC<{
  clientId: string
  firstName: string
  selected: Set<string>
  full: boolean
  onToggle: (f: FileMeta) => void
  onUpload: () => void
}> = ({ clientId, firstName, selected, full, onToggle, onUpload }) => {
  const [docs, setDocs] = useState<DocumentSearchResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  useEffect(() => {
    let alive = true
    fetchDocumentSearchApi({ clientId })
      .then((d) => { if (alive) setDocs(d) })
      .catch((err) => { if (alive) { setError(errMsg(err, 'Couldn’t load documents')); setDocs([]) } })
    return () => { alive = false }
  }, [clientId])
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (docs ?? []).filter((d) => !needle || d.filename.toLowerCase().includes(needle))
  }, [docs, q])
  return (
    <div className={POP} role="dialog" aria-label="Attach a document">
      <button type="button" className={`${OPT} disabled:opacity-60 disabled:cursor-not-allowed`} onClick={onUpload} disabled={full}>
        <IconUpload />
        <span><b className="font-semibold">Upload a new document</b><small className={OPT_SMALL}>Saved to {firstName}&apos;s documents, then attached</small></span>
      </button>
      <div className="h-px bg-casevo-border-soft my-[6px] mx-[2px]" />
      <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-casevo-text-soft px-[10px] pt-[6px] pb-[2px]">{firstName}&apos;s documents</div>
      <label className={POP_SEARCH}>
        <IconSearch />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search documents"
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-casevo-forest placeholder:text-casevo-text-soft text-[13px]"
        />
      </label>
      <div className="max-h-[248px] overflow-auto">
        {docs === null && <div className="px-[10px] py-2 text-[12.5px] text-casevo-text-soft">Loading documents…</div>}
        {error && <div className="px-[10px] py-2 text-[12.5px] text-casevo-danger">{error}</div>}
        {docs !== null && !error && shown.length === 0 && (
          <div className="px-[10px] py-2 text-[12.5px] text-casevo-text-soft">{q ? 'No matching documents.' : 'No documents yet.'}</div>
        )}
        {shown.map((d) => {
          const on = selected.has(d.id)
          const locked = d.caseAccessible === false
          const where = d.caseId ? (d.caseName || d.caseLabel) : 'client'
          if (locked) {
            return (
              <div key={d.id} className={`${OPT} opacity-60 cursor-not-allowed hover:bg-transparent`} title={LOCKED_FILE_TIP} aria-disabled="true">
                <LockIcon className="w-4 h-4" />
                <span>Restricted file<small className={OPT_SMALL}>On a case you can&apos;t open</small></span>
              </div>
            )
          }
          return (
            <button
              key={d.id}
              type="button"
              className={`${OPT} disabled:opacity-60 disabled:cursor-not-allowed`}
              aria-pressed={on}
              disabled={!on && full}
              onClick={() => onToggle({ id: d.id, filename: d.filename, mimeType: d.mimeType, sizeBytes: d.sizeBytes })}
            >
              <FileIcon mime={d.mimeType} size={16} />
              <span className="min-w-0"><span className="block truncate">{d.filename}</span><small className={`${OPT_SMALL} truncate`}>{formatBytes(d.sizeBytes)} · {where}</small></span>
              {on && <span className="ml-auto text-casevo-brand"><IconCheck /></span>}
            </button>
          )
        })}
      </div>
      {full && <div className="px-[10px] pt-[6px] text-[12px] text-casevo-text-soft">Up to {MAX_LINKS} documents per note.</div>}
    </div>
  )
}

// Link case: this client's cases the viewer can open (the list is already
// audience-filtered server-side, so restricted cases never appear here).
const CasePicker: React.FC<{
  firstName: string
  cases: NoteCaseOption[] | null
  selected: Set<string>
  full: boolean
  onToggle: (id: string) => void
}> = ({ firstName, cases, selected, full, onToggle }) => {
  const [q, setQ] = useState('')
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (cases ?? []).filter((c) => !needle || c.label.toLowerCase().includes(needle) || c.ref.toLowerCase().includes(needle))
  }, [cases, q])
  return (
    <div className={POP} role="dialog" aria-label="Link a case">
      <label className={POP_SEARCH}>
        <IconSearch />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${firstName}'s cases`}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-casevo-forest placeholder:text-casevo-text-soft text-[13px]"
        />
      </label>
      <div className="max-h-[260px] overflow-auto">
        {cases === null && <div className="px-[10px] py-2 text-[12.5px] text-casevo-text-soft">Loading cases…</div>}
        {cases !== null && shown.length === 0 && (
          <div className="px-[10px] py-2 text-[12.5px] text-casevo-text-soft">{q ? 'No matching cases.' : `${firstName} has no cases you can open.`}</div>
        )}
        {shown.map((c) => {
          const on = selected.has(c.id)
          return (
            <button key={c.id} type="button" className={`${OPT} disabled:opacity-60 disabled:cursor-not-allowed`} aria-pressed={on} disabled={!on && full} onClick={() => onToggle(c.id)}>
              <IconCase size={16} />
              <span className="min-w-0"><span className="block truncate">{c.label}</span><small className={OPT_SMALL}>{c.ref} · {c.closed ? 'Closed' : 'Open'}</small></span>
              {on && <span className="ml-auto text-casevo-brand"><IconCheck /></span>}
            </button>
          )
        })}
      </div>
      {full && <div className="px-[10px] pt-[6px] text-[12px] text-casevo-text-soft">Up to {MAX_LINKS} linked cases per note.</div>}
    </div>
  )
}

// ── Chips ───────────────────────────────────────────────────
const LockedCaseChip: React.FC = () => (
  <span className={CHIP_LOCKED} role="link" aria-disabled="true" title={LOCKED_CASE_TIP}>
    <LockIcon className="w-[14px] h-[14px]" />
    Restricted case
    <span className="sr-only">({LOCKED_CASE_TIP})</span>
  </span>
)
const LockedFileChip: React.FC = () => (
  <span className={CHIP_LOCKED} aria-disabled="true" title={LOCKED_FILE_TIP}>
    <LockIcon className="w-[14px] h-[14px]" />
    Restricted file
    <span className="sr-only">({LOCKED_FILE_TIP})</span>
  </span>
)
const FileChipBody: React.FC<{ f: { filename: string; mimeType: string; sizeBytes: number }; showSize?: boolean; uploading?: boolean }> = ({ f, showSize = true, uploading }) => (
  <>
    <span className="text-casevo-brand grid place-items-center"><FileIcon mime={f.mimeType} /></span>
    <span className="truncate">{f.filename}</span>
    {uploading ? <span className="text-casevo-text-soft text-[11.5px]">Uploading…</span> : showSize && <span className="text-casevo-text-soft text-[11.5px]">{formatBytes(f.sizeBytes)}</span>}
  </>
)
const CaseChipBody: React.FC<{ label: string; caseRef?: string | null }> = ({ label, caseRef }) => (
  <>
    <span className="text-casevo-brand grid place-items-center"><IconCase /></span>
    <span className="truncate">{label}</span>
    {caseRef && <span className="text-casevo-text-soft text-[11.5px] font-normal tabular-nums">{caseRef}</span>}
  </>
)

// Read-only chips on a note (feed or rail).
const NoteChips: React.FC<{
  attachments: ClientNoteAttachment[]
  caseLinks: ClientNoteCaseLink[]
  onOpenCase: (caseId: string) => void
  onOpenFile: (fileId: string) => void
  onDownloadFile: (fileId: string) => void
  showSize?: boolean
}> = ({ attachments, caseLinks, onOpenCase, onOpenFile, onDownloadFile, showSize = true }) => (
  // Mockup order: locked (case, then file) first, then files, then cases.
  <div className="flex gap-[6px] flex-wrap">
    {caseLinks.filter((l) => !l.canAccess || l.restricted).map((l) => <LockedCaseChip key={l.caseId} />)}
    {attachments.filter((a) => a.restricted).map((a) => <LockedFileChip key={a.id} />)}
    {/* Split chip: the body previews (new tab / download for non-previewable
        types, as before); the trailing ↓ always downloads. */}
    {attachments.filter((a) => !a.restricted).map((a) => (
      <span key={a.id} className={`${CHIP_FILE} !pr-[4px] hover:border-casevo-mid transition-colors`}>
        <button
          type="button"
          className="inline-flex items-center gap-[7px] min-w-0 h-full rounded-[6px] casevo-focus hover:text-casevo-brand"
          onClick={(e) => { e.stopPropagation(); onOpenFile(a.id) }}
          title={`Preview ${a.filename}`}
        >
          <FileChipBody f={a} showSize={showSize} />
        </button>
        <span className="w-px h-[16px] bg-casevo-border-soft" aria-hidden="true" />
        <button
          type="button"
          className="w-[22px] h-[22px] rounded-[5px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand casevo-focus"
          onClick={(e) => { e.stopPropagation(); onDownloadFile(a.id) }}
          aria-label={`Download ${a.filename}`}
          title="Download"
        >
          <IconDownload />
        </button>
      </span>
    ))}
    {caseLinks.filter((l) => l.canAccess && !l.restricted).map((l) => (
      <button key={l.caseId} type="button" className={CHIP_CASE_BTN} onClick={(e) => { e.stopPropagation(); onOpenCase(l.caseId) }} title={`Open ${l.label}`}>
        <CaseChipBody label={l.label} caseRef={l.caseRef} />
      </button>
    ))}
  </div>
)

// ── Attach / link controls (composer + edit) ────────────────
// One component owns both popovers + the hidden file input, so the composer
// and the inline editor get identical behaviour.
interface LinkState {
  files: FileMeta[]
  caseIds: string[]
}
const AttachLinkButtons: React.FC<{
  clientId: string
  firstName: string
  cases: NoteCaseOption[] | null
  state: LinkState
  lockedFiles: number
  lockedCases: number
  compact?: boolean
  onChange: (updater: (s: LinkState) => LinkState) => void
  onError: (msg: string | null) => void
}> = ({ clientId, firstName, cases, state, lockedFiles, lockedCases, compact, onChange, onError }) => {
  const [open, setOpen] = useState<'file' | 'case' | null>(null)
  const fileRef = useRef<HTMLDivElement>(null)
  const caseRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const close = useCallback(() => setOpen(null), [])
  useDismiss(open === 'file', fileRef, close)
  useDismiss(open === 'case', caseRef, close)
  const filesFull = state.files.length + lockedFiles >= MAX_LINKS
  const casesFull = state.caseIds.length + lockedCases >= MAX_LINKS

  const toggleFile = (f: FileMeta): void =>
    onChange((s) => ({
      ...s,
      files: s.files.some((x) => x.id === f.id) ? s.files.filter((x) => x.id !== f.id) : [...s.files, f],
    }))
  const toggleCase = (id: string): void =>
    onChange((s) => ({ ...s, caseIds: s.caseIds.includes(id) ? s.caseIds.filter((x) => x !== id) : [...s.caseIds, id] }))

  // Upload to the CLIENT's documents (flat route), confirm, then attach.
  const upload = async (file: File): Promise<void> => {
    onError(null)
    if (file.size <= 0 || file.size > 1024 * 1024 * 1024) {
      onError('File too large (max 1 GB)')
      return
    }
    const tempId = `uploading:${Date.now()}:${file.name}`
    const mime = file.type || 'application/octet-stream'
    onChange((s) => ({ ...s, files: [...s.files, { id: tempId, filename: file.name, mimeType: mime, sizeBytes: file.size, uploading: true }] }))
    let fileId: string | null = null
    try {
      const provisioned = await requestUploadUrlFlatApi({ clientId, filename: file.name, mimeType: mime, sizeBytes: file.size })
      fileId = provisioned.fileId
      // Plain fetch — an Authorization header would break the presigned URL.
      const put = await fetch(provisioned.uploadUrl, { method: 'PUT', headers: { 'Content-Type': provisioned.mimeType ?? mime }, body: file })
      if (!put.ok) throw new Error(`Upload failed (status ${put.status})`)
      // Best-effort: attach also self-heals a pending file server-side.
      await completeFileUploadApi(provisioned.fileId).catch(() => undefined)
      const done = provisioned.fileId
      onChange((s) => ({ ...s, files: s.files.map((x) => (x.id === tempId ? { ...x, id: done, uploading: false, uploaded: true } : x)) }))
    } catch (err) {
      if (fileId) await deleteFileFlatApi(fileId).catch(() => undefined)
      onChange((s) => ({ ...s, files: s.files.filter((x) => x.id !== tempId) }))
      onError(errMsg(err, 'Upload failed — try again'))
    }
  }

  const selectedFiles = useMemo(() => new Set(state.files.map((f) => f.id)), [state.files])
  const selectedCases = useMemo(() => new Set(state.caseIds), [state.caseIds])
  return (
    <>
      <div className="relative" ref={fileRef}>
        <button type="button" className={BTN_GHOST} aria-haspopup="dialog" aria-expanded={open === 'file'} onClick={() => setOpen((o) => (o === 'file' ? null : 'file'))}>
          <IconClip />{compact ? 'Attach' : 'Attach document'}
        </button>
        {open === 'file' && (
          <FilePicker
            clientId={clientId}
            firstName={firstName}
            selected={selectedFiles}
            full={filesFull}
            onToggle={toggleFile}
            onUpload={() => { setOpen(null); inputRef.current?.click() }}
          />
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => {
            const f = e.target.files?.[0]
            e.target.value = ''
            if (f) void upload(f)
          }}
        />
      </div>
      <div className="relative" ref={caseRef}>
        <button type="button" className={BTN_GHOST} aria-haspopup="dialog" aria-expanded={open === 'case'} onClick={() => setOpen((o) => (o === 'case' ? null : 'case'))}>
          <IconLink />Link case
        </button>
        {open === 'case' && <CasePicker firstName={firstName} cases={cases} selected={selectedCases} full={casesFull} onToggle={toggleCase} />}
      </div>
    </>
  )
}

// Removable chips for a draft (composer pending row / edit box).
const DraftChips: React.FC<{
  state: LinkState
  caseById: Map<string, NoteCaseOption>
  onChange: (updater: (s: LinkState) => LinkState) => void
  showSize?: boolean
}> = ({ state, caseById, onChange, showSize = true }) => (
  <>
    {state.files.map((f) => (
      <span key={f.id} className={CHIP_FILE}>
        <FileChipBody f={f} showSize={showSize} uploading={f.uploading} />
        {!f.uploading && (
          <button type="button" className={CHIP_X} aria-label={`Remove attachment ${f.filename}`} onClick={() => onChange((s) => ({ ...s, files: s.files.filter((x) => x.id !== f.id) }))}>
            <IconX />
          </button>
        )}
      </span>
    ))}
    {state.caseIds.map((id) => {
      const c = caseById.get(id)
      return (
        <span key={id} className={CHIP_CASE}>
          <CaseChipBody label={c?.label ?? 'Linked case'} caseRef={showSize ? c?.ref : null} />
          <button type="button" className={CHIP_X} aria-label={`Unlink case ${c?.label ?? ''}`.trim()} onClick={() => onChange((s) => ({ ...s, caseIds: s.caseIds.filter((x) => x !== id) }))}>
            <IconX />
          </button>
        </span>
      )
    })}
  </>
)

// ── Main component ──────────────────────────────────────────
interface ClientNotesFeedProps {
  clientId: string
  clientFirstName: string
  // This client's cases the viewer can open (for the link picker + chip labels).
  cases: NoteCaseOption[] | null
  // The old shared-document notes (Client.notes) — shown read-only in the rail
  // so nothing written before the feed disappears.
  legacyNotesHtml?: string | null
  onOpenCase: (caseId: string) => void
}

const ClientNotesFeed: React.FC<ClientNotesFeedProps> = ({ clientId, clientFirstName, cases, legacyNotesHtml, onOpenCase }) => {
  const authUser = useCasevoStore((s) => s.authUser)
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const liveName = useLiveUserName()
  const firstName = clientFirstName.trim() || 'this client'
  const isAdmin = authUser?.role === 'admin'

  const [notes, setNotes] = useState<ClientNote[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  // ── Paging ── The server returns ALL pinned notes + the first page of
  // unpinned, then further unpinned pages via nextCursor ("Load more notes").
  // No silent cap: anything beyond the loaded pages is one click away.
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState<string | null>(null)
  // Bumped by every full (re)load — a load-more that resolves after a refresh
  // replaced the list is dropped instead of appending onto the wrong snapshot.
  const loadSeq = useRef(0)
  // How many unpinned rows are currently loaded, so a live refresh re-fetches
  // at least that deep and never collapses a feed the user has expanded.
  const loadedUnpinned = useRef(0)

  const load = useCallback(async (): Promise<void> => {
    const seq = ++loadSeq.current
    try {
      const want = Math.max(NOTES_PAGE, loadedUnpinned.current)
      const first = await listClientNotesApi(clientId, { limit: Math.min(want, NOTES_PAGE_MAX) })
      let list = first.notes
      let cur = first.nextCursor
      let got = list.filter((n) => !n.pinned).length
      while (cur && got < want) {
        if (!mounted.current || seq !== loadSeq.current) return
        const page = await listClientNotesApi(clientId, { cursor: cur, limit: Math.min(want - got, NOTES_PAGE_MAX) })
        list = [...list, ...page.notes]
        got += page.notes.length
        cur = page.nextCursor
      }
      if (!mounted.current || seq !== loadSeq.current) return
      loadedUnpinned.current = got
      setNotes(sortNotes(list))
      setNextCursor(cur)
      setMoreError(null)
      setLoadError(null)
    } catch (err) {
      if (!mounted.current || seq !== loadSeq.current) return
      setLoadError(errMsg(err, 'Couldn’t load notes'))
      setNotes((prev) => prev ?? [])
    }
  }, [clientId])
  useEffect(() => {
    loadedUnpinned.current = 0 // new client → start from one page
    setNextCursor(null)
    void load()
  }, [load])

  const loadMore = async (): Promise<void> => {
    if (!nextCursor || loadingMore) return
    const seq = loadSeq.current
    setLoadingMore(true)
    setMoreError(null)
    try {
      const page = await listClientNotesApi(clientId, { cursor: nextCursor, limit: NOTES_PAGE })
      if (!mounted.current || seq !== loadSeq.current) return
      loadedUnpinned.current += page.notes.length
      setNotes((prev) => {
        const have = new Set((prev ?? []).map((n) => n.id))
        return sortNotes([...(prev ?? []), ...page.notes.filter((n) => !have.has(n.id))])
      })
      setNextCursor(page.nextCursor)
    } catch (err) {
      if (mounted.current) setMoreError(errMsg(err, 'Couldn’t load more notes'))
    } finally {
      if (mounted.current) setLoadingMore(false)
    }
  }

  // A viewer's access to a linked case can change under an open feed: the server
  // decides which notes are restricted, so re-read.
  useLivePermissions(() => { void load() })
  useFirmChange(['cases'], () => { void load() })

  // Live-sync: another member created/edited/pinned/deleted a note on THIS
  // client → refetch (debounced; own echoes are suppressed by presence).
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null
    const off = presence.subscribeEntityChanged((e) => {
      if (e.entity !== 'clientNote' || e.scope?.clientId !== clientId) return
      if (t) clearTimeout(t)
      t = setTimeout(() => { void load() }, 250)
    })
    return () => { off(); if (t) clearTimeout(t) }
  }, [clientId, load])

  const caseById = useMemo(() => new Map((cases ?? []).map((c) => [c.id, c])), [cases])
  // ★ (perf) Sanitise each note's HTML ONCE per loaded list — not on every
  // render. The composer's state lives on this component, so every keystroke
  // re-rendered the feed and ran DOMPurify over every note (twice for pinned
  // ones: feed + rail).
  const safeHtml = useMemo(() => new Map((notes ?? []).map((n) => [n.id, DOMPurify.sanitize(n.content)])), [notes])

  // ── composer ──
  const [html, setHtml] = useState('')
  const [pinNew, setPinNew] = useState(false)
  const [draft, setDraft] = useState<LinkState>({ files: [], caseIds: [] })
  const [posting, setPosting] = useState(false)
  const [composerError, setComposerError] = useState<string | null>(null)
  const uploadingDraft = draft.files.some((f) => f.uploading)
  const canPost = !posting && !uploadingDraft && !isBlankHtml(html)

  const post = async (): Promise<void> => {
    if (!canPost) return
    if (html.length > MAX_CONTENT) { setComposerError('That note is too long.'); return }
    setPosting(true)
    setComposerError(null)
    try {
      const created = await createClientNoteApi(clientId, {
        content: html,
        pinned: pinNew,
        caseIds: draft.caseIds,
        fileIds: draft.files.map((f) => f.id),
        uploadedFileIds: draft.files.filter((f) => f.uploaded).map((f) => f.id),
      })
      if (!mounted.current) return
      setNotes((prev) => sortNotes([created, ...(prev ?? []).filter((n) => n.id !== created.id)]))
      setHtml('')
      setPinNew(false)
      setDraft({ files: [], caseIds: [] })
    } catch (err) {
      if (mounted.current) setComposerError(errMsg(err, 'Couldn’t add the note — try again'))
    } finally {
      if (mounted.current) setPosting(false)
    }
  }

  // ── editing ──
  const [editing, setEditing] = useState<{ id: string; html: string; links: LinkState } | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  // Unsaved-changes guard: a note typed in the composer but not posted, or an
  // edit whose text differs from the saved note. Leaving the client (or this
  // tab), Back or a refresh asks first; so does Cancel / Esc on the edit.
  const plain = (h: string): string => h.replace(/<[^>]*>/g, '').trim()
  const editDirty = !!editing && !savingEdit
    && plain(editing.html) !== plain((notes ?? []).find((n) => n.id === editing.id)?.content ?? '')
  const guardNotes = useUnsavedChangesGuard((!posting && !isBlankHtml(html)) || editDirty)
  const cancelEdit = (): void => { if (editDirty) guardNotes(() => setEditing(null)); else setEditing(null) }
  const [editError, setEditError] = useState<string | null>(null)
  const startEdit = (n: ClientNote): void => {
    setConfirmDelete(null)
    setEditError(null)
    setEditing({
      id: n.id,
      html: n.content,
      links: {
        files: n.attachments.filter((a) => !a.restricted).map((a) => ({ id: a.id, filename: a.filename, mimeType: a.mimeType, sizeBytes: a.sizeBytes })),
        caseIds: n.caseLinks.filter((l) => l.canAccess && !l.restricted).map((l) => l.caseId),
      },
    })
  }
  // A note being edited was deleted by someone else → drop the editor.
  useEffect(() => {
    if (editing && notes && !notes.some((n) => n.id === editing.id)) setEditing(null)
  }, [notes, editing])
  const saveEdit = async (): Promise<void> => {
    if (!editing || savingEdit) return
    if (isBlankHtml(editing.html)) { setEditError('Write something before saving.'); return }
    if (editing.links.files.some((f) => f.uploading)) return
    setSavingEdit(true)
    setEditError(null)
    try {
      // Only what this viewer can see — the server keeps restricted links/files.
      const saved = await updateClientNoteApi(clientId, editing.id, {
        content: editing.html,
        caseIds: editing.links.caseIds,
        fileIds: editing.links.files.map((f) => f.id),
        uploadedFileIds: editing.links.files.filter((f) => f.uploaded).map((f) => f.id),
      })
      if (!mounted.current) return
      setNotes((prev) => sortNotes((prev ?? []).map((n) => (n.id === saved.id ? saved : n))))
      setEditing(null)
    } catch (err) {
      if (mounted.current) setEditError(errMsg(err, 'Couldn’t save — try again'))
    } finally {
      if (mounted.current) setSavingEdit(false)
    }
  }

  // ── pin (anyone; optimistic) ──
  const togglePin = async (n: ClientNote): Promise<void> => {
    const next = !n.pinned
    setActionError(null)
    setNotes((prev) => sortNotes((prev ?? []).map((x) => (x.id === n.id ? { ...x, pinned: next, pinnedAt: next ? new Date().toISOString() : null } : x))))
    try {
      const saved = await updateClientNoteApi(clientId, n.id, { pinned: next })
      if (!mounted.current) return
      setNotes((prev) => sortNotes((prev ?? []).map((x) => (x.id === saved.id ? saved : x))))
    } catch (err) {
      if (!mounted.current) return
      // Revert ONLY this note's pin state — restoring a whole-list snapshot
      // would clobber anything that changed meanwhile (a post, a live refetch).
      setNotes((prev) => sortNotes((prev ?? []).map((x) => (x.id === n.id ? { ...x, pinned: n.pinned, pinnedAt: n.pinnedAt } : x))))
      setActionError(errMsg(err, next ? 'Couldn’t pin the note' : 'Couldn’t unpin the note'))
    }
  }

  // ── delete (author/admin; confirm) ──
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const doDelete = async (id: string): Promise<void> => {
    setDeleting(id)
    setActionError(null)
    try {
      await deleteClientNoteApi(clientId, id)
      if (!mounted.current) return
      setNotes((prev) => (prev ?? []).filter((n) => n.id !== id))
      setConfirmDelete(null)
    } catch (err) {
      if (!mounted.current) return
      // Already deleted (e.g. by a colleague) → the goal is met; just drop it.
      if (statusOf(err) === 404) {
        setNotes((prev) => (prev ?? []).filter((n) => n.id !== id))
        setConfirmDelete(null)
      } else {
        setActionError(errMsg(err, 'Couldn’t delete the note'))
      }
    } finally {
      if (mounted.current) setDeleting(null)
    }
  }

  const openFile = (fileId: string): void => {
    setActionError(null)
    openAttachment(fileId).catch((err) => setActionError(errMsg(err, 'Couldn’t open that document')))
  }
  const downloadFile = (fileId: string): void => {
    setActionError(null)
    downloadAttachment(fileId).catch((err) => setActionError(errMsg(err, 'Couldn’t download that document')))
  }

  // ── author identity (live name + avatar from firmUsers) ──
  const authorOf = (n: ClientNote): { name: string; first: string; last: string; colour?: string; style?: 'solid' | 'soft' | 'icon'; photo?: string | null; isMe: boolean } => {
    const id = n.author?.id ?? null
    const name = liveName(id, n.author?.name ?? 'Former member') || 'Former member'
    const fu = id ? firmUsers.find((u) => u.id === id) : undefined
    const isMe = !!id && id === authUser?.id
    const src = isMe ? authUser : fu
    const [first, ...rest] = name.split(' ')
    return {
      name,
      first: first ?? '',
      last: rest.join(' '),
      colour: src?.avatarColour || n.author?.avatarColour,
      style: src?.avatarStyle,
      photo: src?.avatarPhotoUrl ?? null,
      isMe,
    }
  }
  // ★ Per-note rights — the server's canEdit/canPin/canDelete when present,
  // else the same rules locally (effectivePermissions; admins resolve true).
  //   edit → author/admin · pin → own, or pinOthersNotes · delete → own, or deleteOthersNotes
  const perms = authUser?.effectivePermissions
  const isOwn = (n: ClientNote): boolean => !!n.author && n.author.id === authUser?.id
  const canEdit = (n: ClientNote): boolean => n.canEdit ?? (isAdmin || isOwn(n))
  const canPin = (n: ClientNote): boolean => n.canPin ?? (isOwn(n) || isAdmin || !!perms?.pinOthersNotes)
  const canDelete = (n: ClientNote): boolean => n.canDelete ?? (isOwn(n) || isAdmin || !!perms?.deleteOthersNotes)

  const pinned = (notes ?? []).filter((n) => n.pinned)
  const unpinned = (notes ?? []).filter((n) => !n.pinned)
  const hasLegacy = !!legacyNotesHtml && !isBlankHtml(legacyNotesHtml)

  // ── shared pieces ──
  const actions = (n: ClientNote, size: 'feed' | 'rail'): React.ReactNode => {
    const box = size === 'rail' ? '!w-[26px] !h-[26px] !rounded-[7px]' : ''
    return (
      <div className="ml-auto flex gap-[2px] items-center">
        {canPin(n) && (
          <button
            type="button"
            className={`${IBTN} ${box} ${n.pinned ? IBTN_PIN_ON : ''}`}
            aria-label={n.pinned ? 'Unpin note' : 'Pin note'}
            aria-pressed={n.pinned}
            title={n.pinned ? 'Unpin' : 'Pin'}
            onClick={() => { void togglePin(n) }}
          >
            <IconPin filled={n.pinned} size={size === 'rail' ? 14 : 16} />
          </button>
        )}
        {canEdit(n) && (
          <button type="button" className={`${IBTN} ${box}`} aria-label="Edit note" title="Edit" onClick={() => startEdit(n)}>
            <IconEdit />
          </button>
        )}
        {canDelete(n) && (
          <button type="button" className={`${IBTN} ${box} ${IBTN_DEL}`} aria-label="Delete note" title="Delete" onClick={() => { setEditing(null); setConfirmDelete(n.id) }}>
            <IconTrash />
          </button>
        )}
      </div>
    )
  }

  const editBox = (n: ClientNote, rail: boolean): React.ReactNode => {
    if (!editing || editing.id !== n.id) return null
    const lockedFiles = n.attachments.filter((a) => a.restricted).length
    const lockedCases = n.caseLinks.filter((l) => !l.canAccess || l.restricted).length
    const setLinks = (u: (s: LinkState) => LinkState): void =>
      setEditing((e) => (e ? { ...e, links: u(e.links) } : e))
    return (
      <div
        className={rail ? 'mt-2' : 'mt-[10px] ml-[42px]'}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void saveEdit() }
          else if (e.key === 'Escape') { e.preventDefault(); cancelEdit() }
        }}
      >
        <RichTextEditor
          variant="note"
          autoFocus
          ariaLabel="Edit note"
          value={editing.html}
          onChange={(v) => setEditing((e) => (e ? { ...e, html: v } : e))}
        />
        <div className="flex gap-[6px] flex-wrap mt-[10px]">
          <DraftChips state={editing.links} caseById={caseById} onChange={setLinks} showSize={false} />
          {/* restricted items can't be removed — they're kept on save */}
          {Array.from({ length: lockedFiles }, (_, i) => <LockedFileChip key={`lf${i}`} />)}
          {Array.from({ length: lockedCases }, (_, i) => <LockedCaseChip key={`lc${i}`} />)}
          <AttachLinkButtons
            clientId={clientId}
            firstName={firstName}
            cases={cases}
            state={editing.links}
            lockedFiles={lockedFiles}
            lockedCases={lockedCases}
            compact
            onChange={setLinks}
            onError={setEditError}
          />
        </div>
        <div className="flex gap-2 items-center mt-[10px] flex-wrap">
          <button type="button" className={BTN_PRI} disabled={savingEdit || editing.links.files.some((f) => f.uploading) || isBlankHtml(editing.html)} onClick={() => { void saveEdit() }}>
            {savingEdit ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className={BTN_DEFAULT} onClick={cancelEdit}>Cancel</button>
          <span className="text-[12px] text-casevo-text-soft">Esc to cancel</span>
        </div>
        {editError && <p className="mt-2 text-[12.5px] text-casevo-danger" role="alert">{editError}</p>}
      </div>
    )
  }

  const confirmRow = (n: ClientNote, rail: boolean): React.ReactNode =>
    confirmDelete === n.id && (
      <div
        role="alertdialog"
        aria-label="Delete note"
        className={`${rail ? 'mt-2 flex-wrap' : 'mt-3 ml-[42px]'} flex gap-[10px] items-center px-3 py-[10px] rounded-[10px] bg-[#fdecec] dark:bg-[rgba(127,29,29,0.35)] text-[13px] text-[#8a1c14] dark:text-[#fecaca]`}
      >
        <IconTrash />
        <span>Delete this note? Attached documents stay in {firstName}&apos;s documents.</span>
        <span className="flex-1" />
        <button type="button" className={BTN_DEFAULT} onClick={() => setConfirmDelete(null)} autoFocus>Cancel</button>
        <button type="button" className={BTN_DANGER} disabled={deleting === n.id} onClick={() => { void doDelete(n.id) }}>
          {deleting === n.id ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    )

  // Feed card (.card.note)
  const renderFeedNote = (n: ClientNote): React.ReactNode => {
    const a = authorOf(n)
    const isEditing = editing?.id === n.id
    return (
      <article key={n.id} className={`${CARD} relative px-[18px] py-4 mb-3`} aria-label={`Note by ${a.name}`}>
        <div className="flex items-center gap-[10px]">
          <UserAvatar userId={n.author?.id} fallbackName={a.name} fallbackLook={{ colour: n.author?.avatarColour }} size="sm" title={false} />
          <div className="min-w-0">
            <div className="font-semibold text-[14px] leading-[1.5] text-casevo-forest flex items-center gap-[6px]">
              <span className="truncate">{a.name}</span>
              {a.isMe && <span className="text-[10.5px] font-bold px-[7px] py-px rounded-full bg-casevo-gold text-[#3a2e08] leading-[1.5]">You</span>}
            </div>
            <div className="text-[12.5px] leading-[1.5] text-casevo-text-soft">{formatWhen(n.createdAt)}{n.edited ? ' · edited' : ''}</div>
          </div>
          {!isEditing && actions(n, 'feed')}
        </div>
        {n.restricted ? <RestrictedNoteLine className="mt-[9px] ml-[42px]" label={n.restrictedLabel} /> : isEditing ? editBox(n, false) : (
          <>
            <div className={`mt-[9px] ml-[42px] text-[14px] leading-[1.65] text-casevo-forest break-words ${NOTE_RICH_CLS}`} dangerouslySetInnerHTML={{ __html: safeHtml.get(n.id) ?? DOMPurify.sanitize(n.content) }} />
            {(n.attachments.length > 0 || n.caseLinks.length > 0) && (
              <div className="mt-[10px] ml-[42px]">
                <NoteChips attachments={n.attachments} caseLinks={n.caseLinks} onOpenCase={onOpenCase} onOpenFile={openFile} onDownloadFile={downloadFile} />
              </div>
            )}
            {confirmRow(n, false)}
          </>
        )}
      </article>
    )
  }

  // Rail item (.pinside .pi)
  const renderRailNote = (n: ClientNote, i: number): React.ReactNode => {
    const a = authorOf(n)
    const isEditing = editing?.id === n.id
    return (
      <div key={n.id} className={`px-4 py-3 text-[13px] leading-[1.55] text-casevo-forest ${i > 0 ? 'border-t border-casevo-border-soft' : ''}`}>
        <div className="flex items-center gap-[7px] text-[12px] text-casevo-text-soft mb-1 min-h-[26px]">
          <UserAvatar userId={n.author?.id} fallbackName={a.name} fallbackLook={{ colour: n.author?.avatarColour }} size="rail" title={false} />
          <span className="truncate">{a.name} · {formatShort(n.pinnedAt ?? n.createdAt)}</span>
          {!isEditing && actions(n, 'rail')}
        </div>
        {n.restricted ? <RestrictedNoteLine label={n.restrictedLabel} /> : isEditing ? editBox(n, true) : (
          <>
            <div className={`break-words ${NOTE_RICH_CLS}`} dangerouslySetInnerHTML={{ __html: safeHtml.get(n.id) ?? DOMPurify.sanitize(n.content) }} />
            {(n.attachments.length > 0 || n.caseLinks.length > 0) && (
              <div className="mt-2">
                <NoteChips attachments={n.attachments} caseLinks={n.caseLinks} onOpenCase={onOpenCase} onOpenFile={openFile} onDownloadFile={downloadFile} showSize={false} />
              </div>
            )}
            {confirmRow(n, true)}
          </>
        )}
      </div>
    )
  }

  const me = authUser
  const loading = notes === null

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
      {/* ★ NO LAYOUT SHIFT (below xl): the Pinned panel comes FIRST on narrow
          widths and only exists once the notes load — it used to pop in above
          the composer + feed and push them down. Below xl the feed now waits
          for the notes (this placeholder holds its place); at xl+ the pinned
          panel is a side rail, so the composer shows at once as before. */}
      {loading && (
        <div className="xl:hidden min-w-0" aria-busy="true" aria-label="Loading notes">
          <div className={`${CARD} h-[132px] mb-4 bg-casevo-input/60 motion-safe:animate-pulse`} />
          {[0, 1].map((i) => <div key={i} className={`${CARD} h-[104px] mb-3 motion-safe:animate-pulse`} />)}
        </div>
      )}
      {/* ── FEED ── */}
      <div className={`min-w-0 ${loading ? 'hidden xl:block' : ''}`}>
        {/* Composer (.card.composer) */}
        <div
          className={`${CARD} relative px-[18px] py-4`}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void post() }
          }}
        >
          <div className="flex gap-3 items-start">
            {me && <UserAvatar userId={me.id} fallbackName={`${me.firstName} ${me.lastName}`.trim() || me.email} size="sm" title={false} />}
            <RichTextEditor
              variant="note"
              value={html}
              onChange={setHtml}
              placeholder={`Write a note for the team about ${firstName}…`}
              ariaLabel="New note"
            />
          </div>
          {(draft.files.length > 0 || draft.caseIds.length > 0) && (
            <div className="flex gap-[6px] flex-wrap mt-[10px] ml-[44px]">
              <DraftChips state={draft} caseById={caseById} onChange={(u) => setDraft(u)} />
            </div>
          )}
          <div className="flex items-center gap-2 mt-3 ml-[44px] flex-wrap">
            <AttachLinkButtons
              clientId={clientId}
              firstName={firstName}
              cases={cases}
              state={draft}
              lockedFiles={0}
              lockedCases={0}
              onChange={(u) => setDraft(u)}
              onError={setComposerError}
            />
            <button
              type="button"
              aria-pressed={pinNew}
              onClick={() => setPinNew((p) => !p)}
              className={`inline-flex items-center gap-[7px] h-[30px] px-[11px] rounded-[8px] border text-[12.5px] transition-colors casevo-focus ${
                pinNew
                  ? `border-casevo-gold ${GOLD_TEXT} bg-[rgba(224,182,74,0.12)] font-semibold`
                  : 'border-casevo-border bg-casevo-surface text-casevo-brand hover:border-casevo-mid'
              }`}
            >
              <IconPin filled={pinNew} size={14} />{pinNew ? 'Pinned' : 'Pin'}
            </button>
            <span className="flex-1" />
            <span className="text-[12px] text-casevo-text-soft hidden sm:inline">Ctrl + Enter to post</span>
            <button type="button" className={BTN_PRI} disabled={!canPost} onClick={() => { void post() }}>
              {posting ? 'Posting…' : 'Post note'}
            </button>
          </div>
          {composerError && <p className="mt-2 ml-[44px] text-[12.5px] text-casevo-danger" role="alert">{composerError}</p>}
        </div>

        {actionError && (
          <p className="mt-3 text-[12.5px] text-casevo-danger" role="alert">{actionError}</p>
        )}

        {loading ? (
          <div aria-busy="true" aria-label="Loading notes">
            <div className="flex items-center gap-[10px] mt-[22px] mb-[10px] mx-[2px]">
              <span className="h-3 w-20 rounded bg-casevo-border-soft animate-pulse" /><span className="flex-1 h-px bg-casevo-border-soft" />
            </div>
            {[0, 1].map((i) => (
              <div key={i} className={`${CARD} px-[18px] py-4 mb-3`}>
                <div className="flex items-center gap-[10px]">
                  <span className="w-8 h-8 rounded-full bg-casevo-border-soft animate-pulse" />
                  <div className="flex-1"><div className="h-3 w-32 rounded bg-casevo-border-soft animate-pulse" /><div className="h-3 w-20 mt-2 rounded bg-casevo-border-soft animate-pulse" /></div>
                </div>
                <div className="ml-[42px] mt-3 h-3 w-4/5 rounded bg-casevo-border-soft animate-pulse" />
                <div className="ml-[42px] mt-2 h-3 w-3/5 rounded bg-casevo-border-soft animate-pulse" />
              </div>
            ))}
          </div>
        ) : loadError && (notes ?? []).length === 0 ? (
          <div className={`${CARD} mt-[18px] px-6 py-8 text-center`}>
            <p className="text-[13.5px] text-casevo-danger">{loadError}</p>
            <button type="button" className={`${BTN_DEFAULT} mt-3`} onClick={() => { setNotes(null); void load() }}>Try again</button>
          </div>
        ) : (notes ?? []).length === 0 ? (
          <div className={`${CARD} mt-[18px] px-6 py-12 text-center`}>
            <div className="w-[52px] h-[52px] rounded-[14px] bg-casevo-mint text-casevo-brand grid place-items-center mx-auto mb-[14px]"><IconNote /></div>
            <h3 className="m-0 mb-1 text-[16px] font-semibold text-casevo-forest">No notes yet</h3>
            <p className="mx-auto max-w-[420px] text-[13.5px] text-casevo-text-soft">
              Notes are for the team — who can speak for {firstName}, preferences, anything worth remembering. Attach a document or link one of their cases. Pin the ones everyone should see first.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-[10px] mt-[22px] mb-[10px] mx-[2px]">
              <h3 className="m-0 text-[11.5px] font-bold tracking-[0.16em] uppercase text-casevo-text-soft">All notes</h3>
              <span className="flex-1 h-px bg-casevo-border-soft" />
            </div>
            {unpinned.length === 0 && !nextCursor ? (
              <p className="mx-[2px] text-[13px] text-casevo-text-soft">Every note on {firstName} is pinned — they&apos;re in the Pinned panel.</p>
            ) : unpinned.map(renderFeedNote)}
            {nextCursor && (
              <div className="flex flex-col items-center gap-2 mt-1 mb-2">
                <button type="button" className={BTN_DEFAULT} onClick={() => { void loadMore() }} disabled={loadingMore} aria-busy={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more notes'}
                </button>
                {moreError && <p role="alert" className="m-0 text-[12.5px] text-casevo-danger">{moreError}</p>}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── PINNED RAIL (sticky; first on narrow widths) ── */}
      <aside className="order-first xl:order-none xl:sticky xl:top-[70px] min-w-0" aria-label="Pinned notes">
      <SideScroll from={1280} label="Pinned notes">
        {pinned.length > 0 && (
          <div className={`${CARD} mb-4`}>
            <div className="flex items-center gap-2 px-4 py-[14px] border-b border-casevo-border-soft">
              <IconPin filled size={15} className="text-casevo-gold" />
              <h3 className="m-0 text-[14px] font-semibold text-casevo-forest">Pinned</h3>
              <span className="ml-auto text-[12px] text-casevo-text-soft">{pinned.length}</span>
            </div>
            {pinned.map(renderRailNote)}
          </div>
        )}
        {hasLegacy && (
          <div className={`${CARD} mb-4`}>
            <div className="flex items-center gap-2 px-4 py-[14px] border-b border-casevo-border-soft">
              <IconDoc />
              <h3 className="m-0 text-[14px] font-semibold text-casevo-forest">Earlier shared notes</h3>
              <span className="ml-auto text-[12px] text-casevo-text-soft">Read-only</span>
            </div>
            <div
              className={`px-4 py-3 text-[13px] leading-[1.55] text-casevo-forest max-h-[280px] overflow-auto break-words ${NOTE_RICH_CLS}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(legacyNotesHtml ?? '') }}
            />
          </div>
        )}
        <div className={`${CARD} px-4 py-[14px] text-[12.5px] leading-[1.55] text-casevo-text-soft`}>
          Notes are <b className="font-semibold text-casevo-forest">non-billable</b> team notes — calls, emails and time go in <b className="font-semibold text-casevo-forest">Activity</b>.
        </div>
      </SideScroll>
      </aside>
    </div>
  )
}

export default ClientNotesFeed
